/**
 * Team-level planning for the plan-together flows (the fix the dead-on-paper
 * measurements asked for): a team's first bot to plan a round chooses the whole
 * team's shots jointly, so a teammate's faster shot does not kill or launch the
 * target an earlier, slower shot was aimed at. Each unit brings a few candidate
 * shots (its own best, and its best against another target); every combination
 * is dry-run through the round simulator with the team's already-queued orders,
 * and the assignment the round values most wins. The assignments are remembered
 * on the units (PlayerState.botPlannedShot) and used when their turns come.
 *
 * Pure: the chooser takes candidates and an evaluator; the evaluator is the
 * round simulator's team value (the Phase 49 valuation).
 */
import type { GameState } from '../../types';
import type { BotFireResult } from './types';
import type { RoundOrder } from '../../core/roundSim';
import { simulateRound } from '../../core/roundSim';

export interface TeamCandidate {
  playerId: number;
  /** In preference order: the unit's own best first. */
  options: BotFireResult[];
}

export interface TeamPlanResult {
  assignment: Map<number, BotFireResult>;
  value: number;
  /** Combinations evaluated (capped). */
  evaluated: number;
}

/** Combinations beyond this are not evaluated (3 units × 3 options = 27; 4 × 3 = 81 → capped). */
export const TEAM_PLAN_MAX_COMBINATIONS = 64;

/** Enemy damage, kills and launches by a team's flights in a round record — the Phase 49 valuation. */
export function teamRoundValue(state: GameState, orders: RoundOrder[], teamIndex: number): number {
  const rec = simulateRound(state, orders, { launchStaggerSteps: 0, prepaid: true });
  const teamOf = new Map(state.players.map(p => [p.id, p.teamIndex]));
  let v = 0;
  for (const i of rec.impacts) {
    if (teamOf.get(rec.flights[i.flightIndex].ownerId) !== teamIndex) continue;
    for (const d of i.damages) if (teamOf.get(d.playerId) !== teamIndex) v += d.damage;
    for (const id of i.kills) if (teamOf.get(id) !== teamIndex) v += 40;
    for (const id of i.launched) if (teamOf.get(id) !== teamIndex) v += 10;
  }
  return v;
}

/** A unit's candidate as a round order. */
export function orderFor(playerId: number, fire: BotFireResult): RoundOrder {
  return { playerId, fire: { params: { azimuth: fire.azimuth, elevation: fire.elevation, power: fire.power, weaponKey: fire.weaponKey } } };
}

/**
 * Choose one option per unit maximising `evaluate(assignment)`. Ties keep the earlier combination, so with
 * equal values every unit keeps its own best (the first option) — the plan never changes a shot for nothing.
 */
export function chooseTeamPlan(candidates: readonly TeamCandidate[], evaluate: (assignment: Map<number, BotFireResult>) => number): TeamPlanResult {
  const units = candidates.filter(c => c.options.length > 0);
  if (units.length === 0) return { assignment: new Map(), value: 0, evaluated: 0 };
  const counts = units.map(u => u.options.length);
  const total = counts.reduce((a, b) => a * b, 1);
  const idx = units.map(() => 0);
  let best: Map<number, BotFireResult> | null = null;
  let bestValue = -Infinity;
  let evaluated = 0;
  for (let k = 0; k < Math.min(total, TEAM_PLAN_MAX_COMBINATIONS); k++) {
    const assignment = new Map<number, BotFireResult>();
    units.forEach((u, i) => assignment.set(u.playerId, u.options[idx[i]]));
    const v = evaluate(assignment);
    evaluated++;
    if (v > bestValue) { bestValue = v; best = assignment; }
    // next combination, first unit's option varying slowest so "everyone's own best" is evaluated first
    for (let i = units.length - 1; i >= 0; i--) { idx[i]++; if (idx[i] < counts[i]) break; idx[i] = 0; }
  }
  return { assignment: best!, value: bestValue, evaluated };
}
